import React, { useCallback, useEffect, useState } from 'react';

// Admin → Emails: automations (sequences sent after a trial starts, someone
// joins a team, a trial converts) and the templates they send — edited
// here, not in code. Data: /api/admin/emails (backend/routes/emailAutomations.js).

interface Step { template: string; day: number; skip_if?: string[]; only_if?: string[]; stats?: Record<string, number> }
interface Automation {
  id: number; key: string; name: string; description: string | null; trigger: string;
  steps: Step[]; stop_if: string[]; active: boolean; activated_at: string | null;
  enrolled: Record<string, number>;
}
interface Template { id: number; key: string; name: string; subject: string; preheader: string | null; body: string; cta_label: string | null; cta_url: string | null; updated_at: string }

const TRIGGERS: Record<string, string> = {
  trial_started: 'When a trial starts',
  team_member_joined: 'When someone joins a team',
  trial_converted: 'When a trial becomes a paid plan',
};
const auth = () => ({ Authorization: `Bearer ${window.localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

export const EmailsPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<null | { automations: Automation[]; templates: Template[]; variables: Record<string, string>; conditions: Record<string, string> }>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Template | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    fetch('/api/admin/emails', { headers: auth() })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) setError(d.error || 'Failed to load'); else { setError(''); setData(d); } })
      .catch(() => setError('Failed to load'));
  }, []);
  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const flash = (s: string) => { setNote(s); setTimeout(() => setNote(''), 4000); };

  const toggle = async (a: Automation) => {
    if (!a.active && !window.confirm(`Switch on "${a.name}"?\n\nIt will email people whose trigger happens from now on (not anyone from before), from Alex at MarketMapr.`)) return;
    await fetch(`/api/admin/emails/automations/${a.id}`, { method: 'PATCH', headers: auth(), body: JSON.stringify({ active: !a.active }) });
    load();
  };
  const setDay = async (a: Automation, i: number, day: number) => {
    const steps = a.steps.map((s, j) => ({ template: s.template, day: j === i ? day : s.day, skip_if: s.skip_if, only_if: s.only_if }));
    await fetch(`/api/admin/emails/automations/${a.id}`, { method: 'PATCH', headers: auth(), body: JSON.stringify({ steps }) });
    load();
  };
  const testAutomation = async (a: Automation) => {
    const r = await fetch('/api/admin/emails/test', { method: 'POST', headers: auth(), body: JSON.stringify({ automationId: a.id }) });
    const d = await r.json();
    flash(r.ok ? `Sent ${d.sent} test email${d.sent === 1 ? '' : 's'} to ${d.to}` : (d.error || 'Test failed'));
  };

  const tName = (key: string) => data?.templates.find((t) => t.key === key)?.name || key;
  const cond = (list?: string[]) => (list || []).map((c) => data?.conditions[c] || c).join(', ');

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <button onClick={() => setOpen(!open)} className="w-full p-6 flex justify-between items-center text-left">
        <div>
          <h2 className="text-xl font-semibold">Emails</h2>
          <p className="text-sm text-gray-500 mt-1">Automated sequences (trials, new team members, conversions) and the templates they send — from Alex at MarketMapr, 9:30am UK time</p>
        </div>
        <span className="text-sm text-blue-600">{open ? 'Hide' : 'Open'}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-8">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {note && <p className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">{note}</p>}

          {/* Automations */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Automations</h3>
            <div className="space-y-4">
              {(data?.automations || []).map((a) => (
                <div key={a.id} className={`rounded-lg border ${a.active ? 'border-green-200' : 'border-gray-200'} p-4`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{a.name}</span>
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${a.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{a.active ? 'On' : 'Off'}</span>
                        <span className="text-xs text-gray-500">{TRIGGERS[a.trigger] || a.trigger}</span>
                      </div>
                      {a.description && <p className="text-sm text-gray-500 mt-1">{a.description}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        In it now: {a.enrolled.active || 0} · finished: {a.enrolled.completed || 0} · stopped early: {a.enrolled.stopped || 0}
                        {a.stop_if?.length ? ` · stops if ${cond(a.stop_if)}` : ''}
                        {a.active && a.activated_at ? ` · on since ${new Date(a.activated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => testAutomation(a)} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Send me the whole sequence</button>
                      <button onClick={() => toggle(a)} className={`text-xs px-3 py-1.5 rounded font-medium ${a.active ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-green-600 text-white hover:bg-green-700'}`}>{a.active ? 'Switch off' : 'Switch on'}</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto mt-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide border-b">
                          <th className="py-1.5 pr-3 w-20">Day</th><th className="pr-3">Email</th><th className="pr-3">Rules</th><th className="pr-3 text-right">Sent</th><th className="text-right">Skipped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.steps.map((s, i) => (
                          <tr key={i} className="border-b border-gray-100 align-top">
                            <td className="py-1.5 pr-3">
                              <input type="number" min={0} max={90} defaultValue={s.day} onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v !== s.day && !isNaN(v)) setDay(a, i, v); }}
                                className="w-14 px-1.5 py-0.5 border rounded text-sm tabular-nums" title={s.day === 0 ? 'Day 0 goes straight away' : 'Sent at 9:30am UK time on this day'} />
                            </td>
                            <td className="pr-3">
                              <button onClick={() => setEditing(data?.templates.find((t) => t.key === s.template) || null)} className="text-blue-700 hover:underline text-left">{tName(s.template)}</button>
                            </td>
                            <td className="pr-3 text-xs text-gray-500">
                              {s.only_if?.length ? <div>Only for: {cond(s.only_if)}</div> : null}
                              {s.skip_if?.length ? <div>Skipped if {cond(s.skip_if)}</div> : null}
                            </td>
                            <td className="pr-3 text-right tabular-nums">{s.stats?.sent || 0}{s.stats?.failed ? <span className="text-red-600"> ({s.stats.failed} failed)</span> : null}</td>
                            <td className="text-right tabular-nums text-gray-500">{s.stats?.skipped || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Templates */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Templates</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {(data?.templates || []).map((t) => (
                <button key={t.key} onClick={() => setEditing(t)} className="text-left rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 p-3">
                  <div className="text-sm font-medium text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-500 truncate">{t.subject}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {editing && data && (
        <TemplateEditor
          template={editing}
          variables={data.variables}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); flash(msg); load(); }}
        />
      )}
    </div>
  );
};

// Edit one template: fields on the left, the real email on the right.
const TemplateEditor: React.FC<{ template: Template; variables: Record<string, string>; onClose: () => void; onSaved: (msg: string) => void }> = ({ template, variables, onClose, onSaved }) => {
  const [t, setT] = useState<Template>(template);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [ask, setAsk] = useState('');
  const set = (k: keyof Template) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setT({ ...t, [k]: e.target.value });

  useEffect(() => {
    const h = setTimeout(() => {
      fetch('/api/admin/emails/preview', { method: 'POST', headers: auth(), body: JSON.stringify({ template: t }) })
        .then((r) => r.json()).then((d) => d.html && setPreview(d)).catch(() => {});
    }, 400);
    return () => clearTimeout(h);
  }, [t]);

  const save = async () => {
    setBusy('save');
    const r = await fetch(`/api/admin/emails/templates/${t.key}`, { method: 'PUT', headers: auth(), body: JSON.stringify(t) });
    const d = await r.json();
    setBusy('');
    if (r.ok) onSaved(`Saved “${t.name}”`); else setMsg(d.error || 'Save failed');
  };
  const test = async () => {
    setBusy('test');
    // Save first so the test is what will go out.
    await fetch(`/api/admin/emails/templates/${t.key}`, { method: 'PUT', headers: auth(), body: JSON.stringify(t) });
    const r = await fetch('/api/admin/emails/test', { method: 'POST', headers: auth(), body: JSON.stringify({ templateKey: t.key }) });
    const d = await r.json();
    setBusy('');
    setMsg(r.ok ? `Saved, and a test sent to ${d.to}` : (d.error || 'Test failed'));
  };
  const draft = async () => {
    if (!ask.trim()) return;
    setBusy('draft');
    const r = await fetch('/api/admin/emails/draft', { method: 'POST', headers: auth(), body: JSON.stringify({ prompt: ask, current: { subject: t.subject, body: t.body } }) });
    const d = await r.json();
    setBusy('');
    if (r.ok && d.draft) { setT({ ...t, ...d.draft }); setMsg('Claude’s draft is in — check it, then Save.'); } else setMsg(d.error || 'Drafting failed');
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-stretch justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between gap-3">
          <input value={t.name} onChange={set('name')} className="font-semibold text-gray-900 border-0 focus:ring-0 p-0 flex-1 min-w-0" />
          <div className="flex items-center gap-2">
            <button onClick={test} disabled={!!busy} className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">{busy === 'test' ? 'Sending…' : 'Send me a test'}</button>
            <button onClick={save} disabled={!!busy} className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{busy === 'save' ? 'Saving…' : 'Save'}</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1">×</button>
          </div>
        </div>
        {msg && <div className="px-5 py-2 text-sm bg-blue-50 text-blue-800">{msg}</div>}
        <div className="flex-1 grid md:grid-cols-2 min-h-0">
          <div className="p-5 space-y-3 overflow-y-auto border-r">
            <div className="rounded-lg bg-violet-50 border border-violet-100 p-3">
              <label className="block text-xs font-semibold text-violet-800 mb-1">Draft with Claude</label>
              <div className="flex gap-2">
                <input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="e.g. make it shorter and lead with the Telstra example" className="flex-1 px-2 py-1.5 border rounded text-sm" onKeyDown={(e) => { if (e.key === 'Enter') draft(); }} />
                <button onClick={draft} disabled={!!busy || !ask.trim()} className="text-sm px-3 py-1.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">{busy === 'draft' ? 'Writing…' : 'Draft'}</button>
              </div>
              <p className="text-[11px] text-violet-700 mt-1">Rewrites this email as you ask. Nothing is saved until you press Save.</p>
            </div>
            <label className="block text-xs font-medium text-gray-600">Subject
              <input value={t.subject} onChange={set('subject')} className="mt-1 w-full px-2 py-1.5 border rounded text-sm" />
            </label>
            <label className="block text-xs font-medium text-gray-600">Inbox preview line
              <input value={t.preheader || ''} onChange={set('preheader')} className="mt-1 w-full px-2 py-1.5 border rounded text-sm" />
            </label>
            <label className="block text-xs font-medium text-gray-600">Body
              <textarea value={t.body} onChange={set('body')} rows={18} className="mt-1 w-full px-2 py-1.5 border rounded text-sm font-mono leading-relaxed" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs font-medium text-gray-600">Button text
                <input value={t.cta_label || ''} onChange={set('cta_label')} className="mt-1 w-full px-2 py-1.5 border rounded text-sm" />
              </label>
              <label className="block text-xs font-medium text-gray-600">Button link
                <input value={t.cta_url || ''} onChange={set('cta_url')} className="mt-1 w-full px-2 py-1.5 border rounded text-sm" />
              </label>
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">Formatting and variables</summary>
              <p className="mt-2">Blank line = new paragraph · <code>## Heading</code> · <code>- bullet</code> · <code>**bold**</code> · <code>_italic_</code> · <code>[text](link)</code></p>
              <ul className="mt-1 space-y-0.5">
                {Object.entries(variables).map(([k, v]) => <li key={k}><code>{`{{${k}}}`}</code> — {v}</li>)}
              </ul>
              <p className="mt-1"><code>{'{{first_name|there}}'}</code> uses “there” when there’s no first name. Lesson links: <code>{'{{learn_url}}?lesson=find-hiring-managers'}</code>.</p>
            </details>
          </div>
          <div className="bg-gray-100 flex flex-col min-h-0">
            <div className="px-4 py-2 text-xs text-gray-500 border-b bg-white"><span className="font-medium text-gray-800">{preview?.subject || t.subject}</span> · from Alex at MarketMapr</div>
            {preview ? <iframe title="Email preview" srcDoc={preview.html} className="flex-1 w-full bg-white" sandbox="" /> : <p className="p-4 text-sm text-gray-400">Rendering…</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailsPanel;
